import { Head, router, useForm } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { equipmentLogPath, equipmentLogsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface LogForm {
  id: number
  equipment_id: number
  vendor_id: number | null
  user_id: number | null
  log_date: string
  end_date: string | null
  amount: number
  paid_to: string | null
  paid_by: string | null
  notes: string
  start_odometer: number | null
  end_odometer: number | null
  is_expense: boolean
  approved: boolean
  can_delete: boolean
  has_receipt: boolean
  receipt_filename: string | null
}

interface SelectOption {
  id: number
  name: string
}

interface PaymentMethod {
  value: string
  label: string
}

interface Props {
  log: LogForm
  equipment_options: SelectOption[]
  vendor_options: SelectOption[]
  payment_methods: PaymentMethod[]
  errors?: Record<string, string[]> | null
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Equipment", href: "/equipment" },
  { title: "Maintenance Logs", href: "/equipment_logs" },
  { title: "Edit", href: "#" },
]

export default function Edit({
  log,
  equipment_options,
  vendor_options,
  payment_methods,
  errors,
}: Props) {
  const [vendorId, setVendorId] = useState(log.vendor_id?.toString() ?? "")

  const { data, setData, patch, processing, transform } = useForm({
    equipment_id: log.equipment_id?.toString() ?? "",
    vendor_id: log.vendor_id?.toString() ?? "",
    user_id: log.user_id?.toString() ?? "",
    log_date: log.log_date ? log.log_date.slice(0, 10) : "",
    end_date: log.end_date ? log.end_date.slice(0, 10) : "",
    amount: log.amount?.toString() ?? "0",
    paid_to: log.paid_to ?? "",
    paid_by: log.paid_by ?? "",
    notes: log.notes ?? "",
    start_odometer: log.start_odometer?.toString() ?? "",
    end_odometer: log.end_odometer?.toString() ?? "",
  })

  transform((data) => ({ equipment_log: data }))

  const handleVendorChange = (value: string) => {
    setVendorId(value)
    if (value) {
      setData((prev) => ({ ...prev, vendor_id: value, paid_to: "" }))
    } else {
      setData("vendor_id", value)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(equipmentLogPath(log.id))
  }

  const handleDelete = () => {
    const label = log.is_expense ? "expense" : "log"
    if (confirm(`Are you sure you want to delete this ${label}?`)) {
      router.delete(equipmentLogPath(log.id))
    }
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${log.is_expense ? "Expense" : "Log Entry"}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">
          Edit {log.is_expense ? "Expense" : "Log Entry"}
        </h1>

        {log.approved && (
          <div className="bg-hpi-pale-blue border-hpi-active-blue mb-4 rounded border p-3 text-sm">
            This entry has been approved. Changes will be tracked.
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          {/* Equipment */}
          <div>
            <label className={labelClass}>Equipment</label>
            <select
              value={data.equipment_id}
              onChange={(e) => setData("equipment_id", e.target.value)}
              className={inputClass}
            >
              <option value="">-- Select Equipment --</option>
              {equipment_options.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {eq.name}
                </option>
              ))}
            </select>
            {errors?.equipment_id && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.equipment_id[0]}
              </p>
            )}
          </div>

          {/* Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date</label>
              <input
                type="date"
                value={data.log_date}
                onChange={(e) => setData("log_date", e.target.value)}
                className={inputClass}
              />
              {errors?.log_date && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.log_date[0]}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>End Date</label>
              <input
                type="date"
                value={data.end_date}
                onChange={(e) => setData("end_date", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Vendor */}
          <div>
            <label className={labelClass}>Vendor</label>
            <select
              value={vendorId}
              onChange={(e) => handleVendorChange(e.target.value)}
              className={inputClass}
            >
              <option value="">-- None --</option>
              {vendor_options.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            {log.is_expense && (
              <p className="text-hpi-med-gray mt-1 text-xs">
                Select a vendor or enter &quot;Paid To&quot; below
              </p>
            )}
          </div>

          {/* Odometer */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Start Odometer / Hours</label>
              <input
                type="number"
                step="0.01"
                value={data.start_odometer}
                onChange={(e) => setData("start_odometer", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>End Odometer / Hours</label>
              <input
                type="number"
                step="0.01"
                value={data.end_odometer}
                onChange={(e) => setData("end_odometer", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Expense-only fields */}
          {log.is_expense && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={data.amount}
                    onChange={(e) => setData("amount", e.target.value)}
                    className={inputClass}
                  />
                  {errors?.amount && (
                    <p className="text-hpi-red mt-1 text-sm">
                      {errors.amount[0]}
                    </p>
                  )}
                </div>
                <div>
                  <label className={labelClass}>Payment Method *</label>
                  <select
                    value={data.paid_by}
                    onChange={(e) => setData("paid_by", e.target.value)}
                    className={inputClass}
                  >
                    <option value="">-- Select Payment Method --</option>
                    {payment_methods.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  {errors?.paid_by && (
                    <p className="text-hpi-red mt-1 text-sm">
                      {errors.paid_by[0]}
                    </p>
                  )}
                </div>
              </div>

              {/* Paid To (only when no vendor selected) */}
              {!vendorId && (
                <div>
                  <label className={labelClass}>Paid To *</label>
                  <input
                    type="text"
                    maxLength={100}
                    value={data.paid_to}
                    onChange={(e) => setData("paid_to", e.target.value)}
                    className={inputClass}
                    placeholder="Who was paid?"
                  />
                  {errors?.paid_to && (
                    <p className="text-hpi-red mt-1 text-sm">
                      {errors.paid_to[0]}
                    </p>
                  )}
                </div>
              )}

              {/* Existing receipt info */}
              {log.has_receipt && log.receipt_filename && (
                <div>
                  <label className={labelClass}>Current Receipt</label>
                  <p className="text-hpi-dark-gray mt-1 text-sm">
                    {log.receipt_filename}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Notes */}
          <div>
            <label className={labelClass}>
              {log.is_expense ? "Explain Expense *" : "Notes *"}
            </label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={6}
              className={inputClass}
            />
            {errors?.notes && (
              <p className="text-hpi-red mt-1 text-sm">{errors.notes[0]}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4">
            {log.can_delete ? (
              <button
                type="button"
                onClick={handleDelete}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
              >
                Delete
              </button>
            ) : (
              <div />
            )}
            <div className="space-x-4">
              <a
                href={equipmentLogsPath()}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue inline-block rounded border px-4 py-2"
              >
                Cancel
              </a>
              <button
                type="submit"
                disabled={processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
              >
                {processing ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
